import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * Ungespeicherte Eingaben im Vertragstext-Editor — und der Knopf „Speichern".
 *
 * Die Felder sichern sich beim Verlassen (onBlur) von selbst. Benjamin
 * (28.09.2026): „Ich brauche einen Speichern-Button, auch wenn im Hintergrund
 * zwischengespeichert wird." Ein Knopf, der sagt, ob etwas offen ist, ist die
 * Antwort auf die Frage, die man sich vor dem Schließen des Tabs stellt.
 *
 * Jedes Feld meldet, solange es vom Serverstand abweicht, eine Funktion, die
 * es sichert — und meldet sich ab, sobald es gesichert ist. „Speichern" ruft
 * alle gemeldeten Funktionen. Keine zweite Kopie der Texte: die Felder bleiben
 * die Quelle dessen, was eingetippt wurde.
 */
export type Entwuerfe = {
  /** `sichern` = null meldet das Feld ab (nichts mehr offen). */
  melden: (schluessel: string, sichern: (() => void) | null) => void;
  anzahl: number;
  alleSichern: () => void;
};

export const EntwurfKontext = createContext<Entwuerfe | null>(null);

export function useEntwuerfe(): Entwuerfe {
  const offen = useRef(new Map<string, () => void>());
  const [anzahl, setAnzahl] = useState(0);

  const melden = useCallback((schluessel: string, sichern: (() => void) | null) => {
    if (sichern) offen.current.set(schluessel, sichern);
    else offen.current.delete(schluessel);
    setAnzahl(offen.current.size);
  }, []);

  const alleSichern = useCallback(() => {
    const liste = [...offen.current.values()];
    offen.current.clear();
    setAnzahl(0);
    liste.forEach(f => f());
  }, []);

  // Tab schließen mit offenen Änderungen: der Browser fragt nach.
  useEffect(() => {
    if (anzahl === 0) return;
    const warnen = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warnen);
    return () => window.removeEventListener("beforeunload", warnen);
  }, [anzahl]);

  // Cmd/Strg + S speichert, statt die Seite als Datei abzulegen.
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        (document.activeElement as HTMLElement | null)?.blur();
        alleSichern();
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [alleSichern]);

  return useMemo(() => ({ melden, anzahl, alleSichern }), [melden, anzahl, alleSichern]);
}

/** Für ein Feld: meldet sich an, solange `abweichend`, mit der Funktion `sichern`.
 *  Verschwindet das Feld mit offener Änderung (Zuklappen, Blattwechsel), sichert
 *  es sich beim Abbauen selbst — sonst ginge der Text still verloren. */
export function useEntwurfMelden(schluessel: string, abweichend: boolean, sichern: () => void) {
  const kontext = useContext(EntwurfKontext);
  const melden = kontext?.melden;
  const stand = useRef({ abweichend, sichern });
  stand.current = { abweichend, sichern };

  useEffect(() => {
    if (!melden) return;
    melden(schluessel, abweichend ? sichern : null);
  }, [melden, schluessel, abweichend, sichern]);

  useEffect(() => () => {
    if (stand.current.abweichend) stand.current.sichern();
    melden?.(schluessel, null);
  }, [melden, schluessel]);
}
